import { createUserType, deleteUserType, editUserType } from 'admin/admin.saga';
import * as AdminSelectors from 'admin/admin.selectors';
import { FormikProps } from 'formik';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import * as dto from 'client/dto';
import { nameof } from 'utils/typeHelpers';

import BdFormLabel from 'components/SidebarView/SettingsView/_common/BdFormLabel';
import { FormikRichtextInputDraftjs } from 'components/SidebarView/SettingsView/FormikControls/FormikRichtextInputDraftjs';
import { FormikSemanticInput } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticInput';

import * as UserSelectors from 'user/user.selectors';

import { SimpleCrudTable } from '../_common/SimpleCrudTable';

type UserTypeAdminFM = {
  id: string;
  name: string;
  description: string;
};

const UserTypeAdminFMValidationSchema = Yup.object().shape<Partial<UserTypeAdminFM>>({
  name: Yup.string()
    .required('name is required')
    .max(256, 'name cannot be longer than 256 characters'),
});

// =====================================================================================================================================

const RenderFormikUserTypeAdminCreateNew = ({
  initialValues,
  touched,
  errors,
}: FormikProps<UserTypeAdminFM>) => (
  <>
    <BdFormLabel>Name</BdFormLabel>
    <FormikSemanticInput
      placeholder="Name"
      name={nameof<UserTypeAdminFM>('name')}
      error={touched.name && !!errors.name}
    />
  </>
);

// =====================================================================================================================================

const RenderFormikUserTypeAdmin = ({
  initialValues,
  touched,
  errors,
}: FormikProps<UserTypeAdminFM>) => (
  <>
    <BdFormLabel>Name</BdFormLabel>
    <FormikSemanticInput
      placeholder="Name"
      name={nameof<UserTypeAdminFM>('name')}
      error={touched.name && !!errors.name}
    />
    <BdFormLabel>Notification Description</BdFormLabel>
    <FormikRichtextInputDraftjs
      placeholder="Notification Description"
      name={nameof<UserTypeAdminFM>('description')}
      error={touched.description && !!errors.description}
    />
  </>
);

// =====================================================================================================================================

const UserTypeAdmin: React.FC = () => {
  const dispatch = useDispatch();

  const isLoading = useSelector(AdminSelectors.isAdminLoadingSelector);
  const dataList = useSelector(UserSelectors.rolesSelector);

  const handleItemCreate = useCallback(({ name }: UserTypeAdminFM) => {
    dispatch(createUserType({ name }));
  }, []);

  const handleItemEdit = useCallback(({ id, name, description }: UserTypeAdminFM) => {
    dispatch(editUserType({ id, data: { name, notificationDescription: description } }));
  }, []);

  const handleItemDelete = useCallback(({ id }: dto.UserTypeModel) => {
    dispatch(deleteUserType(id));
  }, []);

  return (
    <div>
      <h1>UserTypeAdmin Page</h1>

      <SimpleCrudTable<dto.UserTypeModel, UserTypeAdminFM>
        loading={isLoading}
        list={dataList}
        entityType="User Type"
        columns={[
          { name: 'Id', getter: x => x.id },
          { name: 'Name', getter: x => x.name },
        ]}
        keyGetter={x => x.id}
        createNew={{
          title: 'Create New User Type',
          validationSchema: UserTypeAdminFMValidationSchema,
          renderForm: RenderFormikUserTypeAdminCreateNew,
          onSubmitForm: handleItemCreate,
        }}
        actions={[
          {
            type: 'custom',
            actionText: 'Edit',
            custom: {
              renderTitle: item => `Edit user type "${item.name}"`,
              renderForm: RenderFormikUserTypeAdmin,
              onSubmitForm: handleItemEdit,
              mapToFormData: (item: dto.UserTypeModel) =>
                ({
                  id: item.id,
                  name: item.name,
                  description: item.notificationDescription,
                } as UserTypeAdminFM),
            },
          },
          {
            type: 'confirm',
            actionText: 'Delete',
            confirm: {
              render: (item: dto.UserTypeModel) =>
                `Are you sure you want to delete the user type "${item.name}"?`,
              onSubmit: handleItemDelete,
            },
          },
        ]}
      />
    </div>
  );
};

export default UserTypeAdmin;
